import React from "react";
import { PRIVACY_URL, TERMS_URL } from "../../data/landing";
import { arrowClass, CTA_MICROCOPY, primaryButtonClass } from "../../utils/buttonStyles";
import { formatUSPhone, toUSDigits } from "../../utils/phone";
import type { Contact, ContactErrors } from "./useApplicationWizard";
const inputClass = "h-12 w-full rounded-[3px] border bg-ink pr-4 text-[16px] text-cream transition-colors duration-150 ease-out focus:outline-none focus:ring-1 focus:ring-gold";
type WizardContactFormProps = {
  contact: Contact;
  errors: ContactErrors;
  onChange: <K extends keyof Contact>(field: K, value: Contact[K]) => void;
  onSubmit: () => void;
};
type TextField = {
  key: "name" | "phone" | "email";
  label: string;
  type: string;
  autoComplete: string;
  inputMode?: "email" | "tel" | "text";
  fullWidth?: boolean;
};
const fields: TextField[] = [{
  key: "name",
  label: "Nombre",
  type: "text",
  autoComplete: "name",
  fullWidth: true
}, {
  key: "phone",
  label: "Teléfono",
  type: "tel",
  autoComplete: "tel",
  inputMode: "tel"
}, {
  key: "email",
  label: "Email",
  type: "email",
  autoComplete: "email",
  inputMode: "email"
}];
export function WizardContactForm({
  contact,
  errors,
  onChange,
  onSubmit
}: WizardContactFormProps) {
  return <form noValidate onSubmit={e => {
    e.preventDefault();
    onSubmit();
  }} data-id="element-41" data-mp="1rmgb08yqclnq">
      <h3 className="font-display text-[24px] uppercase leading-[1.05] tracking-[-0.01em] text-cream sm:text-[30px]" data-id="element-42" data-mp="1mlagc5lieobk">
        ¿A quién le enviamos tu diagnóstico?
      </h3>
      <p className="mt-3 text-[15px] text-muted" data-id="element-43" data-mp="2fcaqkyafoobo">Te toma 20 segundos. No compartimos tus datos.</p>

      <div className="mt-8 grid gap-5 sm:grid-cols-2" data-id="element-44" data-mp="2ab4vwux7qqzi">
        {fields.map(f => {
        const error = errors[f.key];
        const id = `field-${f.key}`;
        const isPhone = f.key === "phone";
        return <div key={f.key} className={f.fullWidth ? "sm:col-span-2" : ""} data-id="element-45" data-mp="tyfzrjrtc93d">
              <label htmlFor={id} className="block font-ui text-[12px] font-bold uppercase tracking-[0.14em] text-muted" data-id="element-46" data-mp="u0izivirpgla">
                {f.label} <span className="text-gold" aria-hidden="true" data-id="element-47" data-mp="10e6duuv32j8w">*</span>
              </label>
              {isPhone ? <div className="relative mt-2" data-id="element-48" data-mp="mh1d9ae52cl1">
                  <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center border-r border-steel pl-4 pr-3 font-ui text-[15px] font-bold text-cream" aria-hidden="true" data-id="element-49" data-mp="rd8lj9u0c065">
                    +1
                  </span>
                  <input id={id} type="tel" inputMode="tel" autoComplete="tel" required placeholder="(786) 555-1234" value={formatUSPhone(contact.phone)} onChange={e => onChange("phone", toUSDigits(e.target.value))} aria-invalid={Boolean(error)} aria-describedby={`${id}-hint${error ? ` ${id}-error` : ""}`} className={`${inputClass} pl-[68px] tabular-nums placeholder:text-steel ${error ? "border-red-400/60" : "border-steel focus:border-gold"}`} data-id="element-50" data-mp="3p8k9fz2krdb" />
                  <span id={`${id}-hint`} className="sr-only" data-id="element-51" data-mp="3g6gpyz7bpypn">
                    Solo números de Estados Unidos, código +1
                  </span>
                </div> : <input id={id} type={f.type} inputMode={f.inputMode} autoComplete={f.autoComplete} required value={contact[f.key]} onChange={e => onChange(f.key, e.target.value)} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined} className={`${inputClass} mt-2 px-4 ${error ? "border-red-400/60" : "border-steel focus:border-gold"}`} data-id="element-52" data-mp="3p86pytf9u5z1" />}
              {error && <p id={`${id}-error`} className="mt-1.5 text-[14px] text-red-300" data-id="element-53" data-mp="2g03f3ayxo3tk">
                  {error}
                </p>}
            </div>;
      })}
      </div>

      <div className="mt-7 border-t border-line pt-6" data-id="element-54" data-mp="1nb5zjfm3wl4i">
        <label className="flex cursor-pointer gap-3" data-id="element-55" data-mp="1ncqrqyky7rz7">
          <input type="checkbox" checked={contact.consent} onChange={e => onChange("consent", e.target.checked)} aria-invalid={Boolean(errors.consent)} aria-describedby={errors.consent ? "consent-error" : undefined} className="mt-1 h-4 w-4 shrink-0 cursor-pointer rounded-sm accent-[#FFB700]" data-id="element-56" data-mp="1sa0tndpixw3x" />
          <span className="text-[14px] leading-relaxed text-muted" data-id="element-57" data-mp="38qvai686cofu">
            Acepto recibir llamadas, mensajes de texto y emails de Studio Web VIP sobre
            mi solicitud. Pueden aplicar tarifas de mensajes y datos. Responde STOP para
            cancelar.{" "}
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="text-cream underline decoration-gold/70 underline-offset-2 hover:text-gold" data-id="element-58" data-mp="x1yrtxzlu7p0">
              Política de Privacidad
            </a>{" "}
            ·{" "}
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="text-cream underline decoration-gold/70 underline-offset-2 hover:text-gold" data-id="element-59" data-mp="ia6efrjdf3px">
              Términos
            </a>
          </span>
        </label>
        {errors.consent && <p id="consent-error" className="mt-1.5 pl-7 text-[14px] text-red-300" data-id="element-60" data-mp="23pc3yrgky0ih">
            {errors.consent}
          </p>}
      </div>

      <div className="mt-8 flex flex-col gap-3" data-id="element-61" data-mp="1yr26ubbaopu8">
        <button type="submit" className={`${primaryButtonClass} w-full`} data-id="element-62" data-mp="2hve1rklgam3c">
          Enviar mi solicitud
          <span className={arrowClass} aria-hidden="true" data-id="element-63" data-mp="2mmdzgs2e986w">
            →
          </span>
        </button>
        <p className="text-center text-[14px] text-muted" data-id="element-64" data-mp="28uzdccd6wqvm">{CTA_MICROCOPY}</p>
      </div>
    </form>;
}